import React, { useState } from 'react';
import { format } from 'date-fns';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  X, Download, Pencil, Trash2, Eye, EyeOff,
  Tag, Link2, History, FileText, Save, XCircle,
  Upload, ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getFileIcon, formatFileSize, isPreviewable, AUDIT_ACTIONS } from './fileConfig';

export const FilePreviewPanel = ({
  file,
  categories = [],
  onClose,
  onUpdate,
  onDelete,
  onDownload,
  onUploadVersion
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    file_name: file?.file_name || '',
    category_id: file?.category_id || '',
    visibility: file?.visibility || 'internal',
    tags: file?.tags?.join(', ') || '',
    notes: file?.notes || ''
  });
  const [activeTab, setActiveTab] = useState('details');

  if (!file) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8">
        <FileText className="h-12 w-12 mb-4 opacity-50" />
        <p className="font-medium">Select a file to preview</p>
        <p className="text-sm">Click on any file to see details</p>
      </div>
    );
  }

  const FileIcon = getFileIcon(file.file_type);

  const handleSave = () => {
    const updates = {
      file_name: editForm.file_name,
      category_id: editForm.category_id || null,
      visibility: editForm.visibility,
      tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      notes: editForm.notes
    };
    onUpdate(file.id, updates);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditForm({
      file_name: file.file_name,
      category_id: file.category_id || '',
      visibility: file.visibility,
      tags: file.tags?.join(', ') || '',
      notes: file.notes || ''
    });
    setIsEditing(false);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'versions', label: `Versions (${file.versions_count || 0})`, icon: History },
    { id: 'links', label: `Links (${file.linked_entities_count || 0})`, icon: Link2 },
    { id: 'activity', label: 'Activity', icon: History }
  ];

  return (
    <div className="flex flex-col h-full border-l bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Badge variant={file.visibility === 'customer' ? 'default' : 'secondary'}>
            {file.visibility === 'customer' ? (
              <><Eye className="h-3 w-3 mr-1" /> Customer</>
            ) : (
              <><EyeOff className="h-3 w-3 mr-1" /> Internal</>
            )}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDownload(file)}>
            <Download className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Preview Area */}
      <div className="p-4 border-b bg-muted/30">
        {isPreviewable(file.file_type) && file.preview_url ? (
          <div className="relative aspect-video bg-black/5 rounded-lg overflow-hidden flex items-center justify-center">
            {file.file_type?.startsWith('image/') ? (
              <img
                src={file.download_url}
                alt={file.file_name}
                className="max-w-full max-h-full object-contain"
              />
            ) : file.file_type === 'application/pdf' ? (
              <iframe
                src={file.download_url}
                className="w-full h-[300px]"
                title={file.file_name}
              />
            ) : (
              <div className="text-center p-8">
                <FileIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Preview not available</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => onDownload(file)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download to view
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <FileIcon className="h-16 w-16 mb-4 text-muted-foreground" />
            <p className="font-medium">{file.file_name}</p>
            <p className="text-sm text-muted-foreground">{formatFileSize(file.size_bytes)}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => onDownload(file)}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b px-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors",
              activeTab === tab.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* File Name */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">File Name</label>
                {isEditing ? (
                  <Input
                    value={editForm.file_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, file_name: e.target.value }))}
                    className="mt-1"
                  />
                ) : (
                  <p className="font-medium mt-1">{file.file_name}</p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Category</label>
                {isEditing ? (
                  <Select
                    value={editForm.category_id || 'none'}
                    onValueChange={(val) => setEditForm(prev => ({ ...prev, category_id: val === 'none' ? '' : val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                            {cat.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1">
                    {file.category_name ? (
                      <Badge variant="outline">{file.category_name}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">Uncategorized</span>
                    )}
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Visibility</label>
                {isEditing ? (
                  <Select
                    value={editForm.visibility}
                    onValueChange={(val) => setEditForm(prev => ({ ...prev, visibility: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal Only</SelectItem>
                      <SelectItem value="customer">Customer Visible</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1">
                    <Badge variant={file.visibility === 'customer' ? 'default' : 'secondary'}>
                      {file.visibility === 'customer' ? 'Customer Visible' : 'Internal Only'}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Tags</label>
                {isEditing ? (
                  <Input
                    value={editForm.tags}
                    onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="Enter tags separated by commas"
                    className="mt-1"
                  />
                ) : (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {file.tags?.length > 0 ? (
                      file.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary">{tag}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No tags</span>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Notes</label>
                {isEditing ? (
                  <Textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add notes..."
                    className="mt-1"
                    rows={3}
                  />
                ) : (
                  <p className="text-sm mt-1">
                    {file.notes || <span className="text-muted-foreground">No notes</span>}
                  </p>
                )}
              </div>

              {/* Edit/Save Buttons */}
              <div className="flex gap-2 pt-2">
                {isEditing ? (
                  <>
                    <Button onClick={handleSave} size="sm">
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Button onClick={handleCancel} variant="outline" size="sm">
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>

              <Separator />

              {/* Meta Info */}
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Size</span>
                  <span>{formatFileSize(file.size_bytes)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Type</span>
                  <span>{file.file_type}</span>
                </div>
                <div className="flex justify-between">
                  <span>Uploaded by</span>
                  <span>{file.uploaded_by_name || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Uploaded</span>
                  <span>{file.uploaded_at ? format(new Date(file.uploaded_at), 'MMM d, yyyy h:mm a') : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span>Version</span>
                  <span>v{file.current_version || 1}</span>
                </div>
              </div>

              <Separator />

              {/* Actions */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => onUploadVersion(file)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload New Version
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-destructive hover:text-destructive"
                  onClick={() => onDelete(file)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete File
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="space-y-3">
              {(file.versions || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No version history</p>
              ) : (
                [...(file.versions || [])].reverse().map((version, idx) => (
                  <div key={version.id} className="flex items-start gap-3 p-3 rounded-lg border">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                      v{version.version_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{version.original_filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(version.size_bytes)} • {version.uploaded_at ? format(new Date(version.uploaded_at), 'MMM d, yyyy') : ''}
                      </p>
                      {version.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{version.notes}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'links' && (
            <div className="space-y-3">
              {(file.links || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No linked entities</p>
              ) : (
                file.links.map((link) => (
                  <div key={link.id} className="flex items-center gap-3 p-3 rounded-lg border">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{link.entity_name || link.entity_id}</p>
                      <p className="text-xs text-muted-foreground capitalize">{link.entity_type}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3">
              {(file.audit_log || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No activity yet</p>
              ) : (
                [...(file.audit_log || [])].reverse().map((entry) => {
                  const actionConfig = AUDIT_ACTIONS[entry.action] || { label: entry.action, color: 'text-muted-foreground' };
                  return (
                    <div key={entry.id} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm">
                          <span className="font-medium">{entry.actor_name || 'Someone'}</span>
                          {' '}
                          <span className={actionConfig.color}>{actionConfig.label}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, h:mm a') : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
